import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import {
  type Aggregation,
  type MetricTrendData,
  type MetricTrendScope,
  useMetricTrend,
} from './data/api';
import { formatTrendValue, rangeDayCount, rangeLabel, trendHeadline } from './data/format';
import { TREND_ICON } from './data/presentation';
import { CHART_CARD_HEIGHT, MetricTrendChart } from './MetricTrendChart';

const EYEBROW_SX = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

const NOWRAP_SX = {
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

const AGGREGATION_LABEL = { total: 'Total', average: 'Daily avg' } as const;

const HEADER_BLOCK_HEIGHT = 108;

const PANEL_MIN_HEIGHT = HEADER_BLOCK_HEIGHT + CHART_CARD_HEIGHT;

export const TREND_CARD_WIDTH = 430;

export interface MetricTrendPanelProps {
  scope: Omit<MetricTrendScope, 'metric'>;
  metric: { name: string; aggregation: Aggregation };
  currencySymbol: string;
}

function periodSummary(trend: MetricTrendData): string {
  const currentDays = rangeDayCount(trend.current.dateRange);
  const previousDays = rangeDayCount(trend.previous.dateRange);
  const counts =
    currentDays === null || previousDays === null
      ? ''
      : `Last ${currentDays} days vs previous ${previousDays} days`;
  const ranges = `${rangeLabel(trend.current.dateRange)} vs ${rangeLabel(trend.previous.dateRange)}`;
  return [counts, ranges].filter((part) => part !== '').join(' · ');
}

function TrendSummary({
  trend,
  aggregation,
  currencySymbol,
}: {
  trend: MetricTrendData;
  aggregation: Aggregation;
  currencySymbol: string;
}): ReactNode {
  const HeadlineIcon = TREND_ICON[trend.direction];
  const headlineColor =
    trend.sentiment === 'good'
      ? 'success.main'
      : trend.sentiment === 'bad'
        ? 'error.dark'
        : 'text.primary';
  const format = (value: number | null): string =>
    formatTrendValue(trend.name, value, trend.format, currencySymbol);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <HeadlineIcon sx={{ fontSize: 17, color: headlineColor, flexShrink: 0 }} />
        <Typography sx={{ ...NOWRAP_SX, fontSize: 14, fontWeight: 700, color: headlineColor }}>
          {trendHeadline(trend.label, trend.delta, trend.direction)}
        </Typography>
      </Box>
      <Typography sx={{ ...NOWRAP_SX, fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
        {periodSummary(trend)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, mt: 2, mb: 2 }}>
        <Box>
          <Typography sx={EYEBROW_SX}>{`Current · ${AGGREGATION_LABEL[aggregation]}`}</Typography>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 800,
              color: 'text.primary',
            }}
          >
            {format(trend.current.total)}
          </Typography>
        </Box>
        <ArrowForwardIcon sx={{ fontSize: 15, color: 'text.secondary', mb: 0.75 }} />
        <Box>
          <Typography sx={EYEBROW_SX}>{`Previous · ${AGGREGATION_LABEL[aggregation]}`}</Typography>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 800,
              color: 'text.secondary',
            }}
          >
            {format(trend.previous.total)}
          </Typography>
        </Box>
      </Box>
      <MetricTrendChart trend={trend} currencySymbol={currencySymbol} />
    </>
  );
}

function TrendSkeleton(): ReactNode {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="circular" width={17} height={17} />
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          <Skeleton variant="text" width={160} />
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, mt: 0.5 }}>
        <Skeleton variant="text" width="70%" />
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, mt: 2, mb: 2 }}>
        <Box>
          <Typography sx={EYEBROW_SX}>
            <Skeleton variant="text" width={60} />
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
            <Skeleton variant="text" width={70} />
          </Typography>
        </Box>
        <ArrowForwardIcon sx={{ fontSize: 15, color: 'text.secondary', mb: 0.75, opacity: 0.3 }} />
        <Box>
          <Typography sx={EYEBROW_SX}>
            <Skeleton variant="text" width={60} />
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
            <Skeleton variant="text" width={70} />
          </Typography>
        </Box>
      </Box>
      <Skeleton variant="rounded" height={CHART_CARD_HEIGHT} />
    </>
  );
}

export function MetricTrendPanel({
  scope,
  metric,
  currencySymbol,
}: MetricTrendPanelProps): ReactNode {
  const { data, error } = useMetricTrend({ ...scope, metric }, true);

  if (error) return <Alert severity="error">{error.message}</Alert>;
  return (
    <Box sx={{ minHeight: PANEL_MIN_HEIGHT }}>
      {data ? (
        <TrendSummary
          trend={data}
          aggregation={metric.aggregation}
          currencySymbol={currencySymbol}
        />
      ) : (
        <TrendSkeleton />
      )}
    </Box>
  );
}
